import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const HumanResourceServices = () => {
  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Organic gradient on the right with animations */}
      <div className="absolute -top-20 -right-20 w-[800px] h-[800px]">
        <div 
          className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-indigo-500/30 via-purple-500/25 to-blue-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatSlow 10s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute top-60 right-40 w-[500px] h-[500px] bg-gradient-to-tl from-purple-400/25 via-indigo-400/20 to-violet-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftRight 12s ease-in-out infinite'
          }}
        ></div>
      </div>

      {/* Organic gradient on the bottom left with animations */}
      <div className="absolute -bottom-40 -left-40 w-[900px] h-[900px]">
        <div 
          className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-br from-purple-500/35 via-indigo-500/25 to-violet-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatLeft 14s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute bottom-40 left-40 w-[600px] h-[600px] bg-gradient-to-tl from-indigo-400/30 via-purple-400/20 to-blue-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftLeft 8s ease-in-out infinite'
          }}
        ></div>
      </div>

      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-32 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-left">
            <div className="mb-8">
              <span className="text-white text-sm font-semibold tracking-wider uppercase opacity-90">FOCUS AREAS</span>
            </div>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-light text-white leading-tight">
              Human Resource Management
            </h1>
            <p className="text-xl text-gray-300 mt-8 max-w-3xl font-light leading-relaxed">
              Comprehensive HR solutions including HRIS, payroll management, and compliance services to streamline your workforce operations.
            </p>
          </div>
        </div>
      </section>

      {/* Services Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column - Header */}
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white font-AfacadFluxLight leading-tight mb-8">
                Streamlining HR operations with comprehensive solutions
              </h2>
            </div>
            {/* Right column - Content */}
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                Our comprehensive HR management services are designed to handle the complex landscape of workforce management while you focus on growing your business. We provide <span className="text-purple-300 font-medium">advanced HRIS solutions</span>, ensuring streamlined employee management and automated HR processes across multiple regions.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                From <span className="text-purple-300 font-medium">payroll management and submissions</span> to comprehensive <span className="text-purple-300 font-medium">compliance and tax management</span>, we handle the administrative complexities so your team can concentrate on strategic growth and employee development.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                With our proven HRIS platform trusted by clients across the USA, Australia, UAE, India, and Sri Lanka, we ensure your HR operations stay efficient, compliant, and focused on what matters most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto">
            {/* Services Grid */}
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 text-left">
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Human Resources Information System (HRIS)</h3>
              <p className="text-gray-300 font-light">A renowned HRIS platform trusted by clients across the USA, Australia, UAE, India, and Sri Lanka, streamlining employee management and automating HR tasks.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Centralized employee data management
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Automated payroll & benefits tracking
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Automated employee onboarding
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Performance tracking & analytics
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Leave and attendance management
                </li>
              </ul>
              </div>
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Payroll Management & Submissions</h3>
              <p className="text-gray-300 font-light">Comprehensive payroll services ensuring accuracy, compliance, and timely delivery while removing administrative burdens from your team.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Salary calculations & benefits processing
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Bank & government registrations
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Secure salary disbursements
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  EPF/ETF/PAYE submissions
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Employee registration for statutory deductions
                </li>
              </ul>
              </div>
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Compliance & Tax Management</h3>
              <p className="text-gray-300 font-light">Complete statutory requirements management to keep your business fully compliant with Sri Lankan regulations and international standards.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  EPF/ETF/PAYE registration for employees
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Legal requirements compliance
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  EPF/ETF/IRD submissions
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Tax calculation & reporting
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Direct liaison with IRD
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed HR Services Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">COMPREHENSIVE HR SOLUTIONS</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              Advanced HR management services for modern businesses
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">HRIS Platform Features</h3>
                <p className="text-gray-300 font-light mb-6">Our renowned HRIS platform provides comprehensive employee management capabilities with automated processes and multi-region compliance support.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Centralized employee data management
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Automated payroll & benefits tracking
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Performance tracking & analytics
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Leave and attendance management
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Payroll Processing Excellence</h3>
                <p className="text-gray-300 font-light mb-6">Comprehensive payroll services ensuring accuracy, compliance, and timely delivery with seamless bank integrations and government submissions.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Salary calculations & benefits processing
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Bank & government registrations
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Secure salary disbursements
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    EPF/ETF/PAYE submissions
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Compliance Management</h3>
                <p className="text-gray-300 font-light mb-6">Complete statutory requirements management ensuring full compliance with Sri Lankan regulations and international standards.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    EPF/ETF/PAYE registration
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Legal requirements compliance
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Tax calculation & reporting
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Direct liaison with IRD
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Multi-Region Support</h3>
                <p className="text-gray-300 font-light mb-6">Trusted by clients across the USA, Australia, UAE, India, and Sri Lanka, our platform handles complex multi-jurisdictional compliance requirements.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    International compliance standards
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Multi-currency payroll support
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Regional tax management
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Cross-border employee management
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              We back founders from idea to seed - and beyond, goal after goal
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16 lg:gap-20">
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Expert Team</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Certified HR professionals with years of industry experience in startup and international environments.</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Customized Solutions</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Tailored HR strategies that align with your business goals and company culture.</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Proven Results</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Track record of improving employee satisfaction, retention, and organizational performance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight max-w-4xl font-AfacadFluxLight">
              How it Works
            </h2>
          </div>
          
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              centeredSlides={true}
              navigation={{
                nextEl: '.swiper-button-next-custom-hr',
                prevEl: '.swiper-button-prev-custom-hr'
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination-custom-hr'
              }}
              breakpoints={{
                640: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              className="h-auto"
            >
              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">1. ASSESS</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Evaluate Your HR Needs
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8">
                    We conduct a comprehensive assessment of your current HR processes, payroll systems, and compliance requirements to identify areas for improvement and optimization.
                  </p>
                  <div className="flex justify-center">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">2. IMPLEMENT</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Deploy HRIS & Payroll Solutions
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8">
                    We implement our proven HRIS platform and set up comprehensive payroll management systems, ensuring seamless integration with your existing business processes.
                  </p>
                  <div className="flex justify-center">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">3. MAINTAIN</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Ongoing Support & Compliance
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8">
                    We provide continuous monitoring, compliance management, and system optimization to ensure your HR operations remain efficient and fully compliant with all regulations.
                  </p>
                  <div className="flex justify-center">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>

            {/* Custom Navigation Buttons */}
            <div className="swiper-button-prev-custom-hr absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              </div>
            <div className="swiper-button-next-custom-hr absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Custom Pagination */}
            <div className="swiper-pagination-custom-hr mt-8 flex justify-center"></div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      {/* <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">INDUSTRIES WE SERVE</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              HR expertise across diverse business sectors
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Technology</h3>
              <p className="text-gray-300 text-sm">SaaS, fintech, and emerging tech</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Healthcare</h3>
              <p className="text-gray-300 text-sm">Medical practices and health tech</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Financial Services</h3>
              <p className="text-gray-300 text-sm">Investment firms and banks</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Professional Services</h3>
              <p className="text-gray-300 text-sm">Consulting and advisory firms</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Manufacturing</h3>
              <p className="text-gray-300 text-sm">Production and industrial</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">E-commerce</h3>
              <p className="text-gray-300 text-sm">Online retail and marketplaces</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight mb-8">
            Ready to Transform Your HR Operations?
          </h2>
          <p className="text-xl text-gray-300 mb-12 font-light">
            Let our HR experts help you build a stronger, more engaged workforce
          </p>
          <div className="flex justify-center">
            <Link to="/contact" className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-10 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl font-AfacadFluxLight uppercase tracking-wide">
              Get Started Today
            </Link>
          </div>
        </div>
      </section>


      {/* CSS Animations */}
      <style jsx>{`
        @keyframes floatSlow {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(80px, -100px) rotate(3deg);
          }
          66% {
            transform: translate(-60px, 60px) rotate(-2deg);
          }
        }

        @keyframes driftRight {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(40px, -60px) scale(1.1);
          }
          50% {
            transform: translate(-32px, 32px) scale(0.88);
          }
          75% {
            transform: translate(48px, 40px) scale(1.06);
          }
        }

        @keyframes floatLeft {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(-72px, 88px) rotate(-3deg);
          }
          66% {
            transform: translate(100px, -72px) rotate(2.5deg);
          }
        }

        @keyframes driftLeft {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(-48px, 64px) scale(1.12);
          }
          50% {
            transform: translate(32px, -40px) scale(0.84);
          }
          75% {
            transform: translate(-56px, -32px) scale(1.08);
          }
        }
      `}</style>
    </div>
  );
};

export default HumanResourceServices; 